import { HabitSchedule, TaskType, WeekDays } from '@griply/types';
import { getActiveSchedule, getScheduleLabel } from '@griply/utils';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Repeat } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { RepeatForm } from 'shared/components/ui/repeat-rrule-form';
import {
  Colors,
  TaskMetaInteraction,
} from 'shared/components/ui/task-meta-interaction';
import { useToday } from 'shared/contexts/today';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { InteractionType } from 'shared/types/data-type';

import * as Styled from './task-meta-interaction-repeat.style';

export type TaskMetaInteractionRepeatProps = Omit<
  React.ComponentProps<typeof TaskMetaInteraction>,
  'start' | 'onClick' | 'label' | 'selected' | 'tooltipLabel'
> & {
  value: HabitSchedule[] | null;
  type: TaskType;
  onChange: (changes: {
    type: TaskType;
    schedules: HabitSchedule[] | null;
  }) => void;
  weekStartsOn: WeekDays;
  targetAllowed: boolean;
  changeToHabitAllowed: boolean;
  onPremium: () => void;
};

export const TaskMetaInteractionRepeat: React.FC<
  TaskMetaInteractionRepeatProps
> = ({
  value,
  type,
  onChange,
  weekStartsOn,
  targetAllowed,
  changeToHabitAllowed,
  onPremium,

  ...rest
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const today = useToday();

  const [adjustedType, setAdjustedType] = useState<
    TaskType.Habit | TaskType.Repeating
  >(
    type === TaskType.Task
      ? changeToHabitAllowed
        ? TaskType.Habit
        : TaskType.Repeating
      : type,
  );
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const val = getActiveSchedule(value, today);

  const _onChangeType = (type: TaskType.Habit | TaskType.Repeating) => {
    if (type === TaskType.Habit && !changeToHabitAllowed) {
      onPremium();
      return;
    }

    setAdjustedType(type);
  };

  const _clear = () => onChange({ type: TaskType.Task, schedules: null });

  const _onChange = (schedule: HabitSchedule | null) => {
    if (!schedule) {
      _clear();
      closeMenu();
      return;
    }

    const endedSchedules = value?.filter(({ endDate }) => !!endDate) ?? [];

    onChange({
      type: adjustedType,
      schedules: [...endedSchedules, schedule],
    });
    closeMenu();
  };

  return (
    <Styled.Container ref={containerRef}>
      <TaskMetaInteraction
        start={<Icon icon={Repeat} />}
        color={val ? Colors.TextPrimary : Colors.Inherit}
        selected={menuOpen}
        onClick={openMenu}
        label={val ? getScheduleLabel(val, t) : undefined}
        tooltipLabel={t(
          val
            ? 'task.repeat.tooltip.edit.label'
            : 'task.repeat.tooltip.add.label',
        )}
        {...rest}
      />

      {menuOpen && (
        <RepeatForm
          initialRrule={val?.rrule?.format}
          scheduleStartDate={val?.startDate}
          initialFrequency={val?.frequency}
          weekStartsOn={weekStartsOn}
          type={adjustedType}
          onChange={_onChange}
          onChangeType={_onChangeType}
          isTargetPremium={!targetAllowed}
          onPremium={onPremium}
          isCreateHabitPremium={!changeToHabitAllowed}
          onClose={closeMenu}
          referenceElement={containerRef}
          interactionType={InteractionType.Task}
        />
      )}
    </Styled.Container>
  );
};
